import { useSession } from '@side/next-auth/react';
import { type SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { TrackOfferEvent } from 'src/modules/analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from 'src/modules/analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { useAgentDslFilters } from '@/common/hooks/useAgentDslFilters';
import { useUserTeamMembers } from '@/common/hooks/useUserTeamMembers';
import { formatTeamMemberId } from '@/common/utils/formatTeamMemberId';
import { getCrossLicensedLocale } from '@/common/utils/license';
import { MAX_CLIENT_GROUP_AGENTS } from '@/constants';
import type { ContactSuggestion } from '$contact-suggestions';
import {
  AgentAutocomplete,
  type AgentSelectOption,
  DEFAULT_VALUE,
} from '$form-fields';
import { useAgentAutocompleteOptions } from '$form-fields/AgentAutocomplete/AgentAutocomplete.helpers';
import { CoAgentAutocomplete } from '$form-fields/AgentAutocomplete/CoAgentAutocomplete';
import type { CreateOfferFormData } from '../../types';
import { NewBuyerFormGroup } from './NewBuyerFormGroup';

const NAME = 'buyerAgents';

interface AgentAutocompleteFieldProps {
  index: number;
  state: string;
  excludeAgentIds: string[];
}

type AutocompleteValue = AgentSelectOption | undefined;

function getAvailableStatesForAgent(propertyState: string, isPrimary: boolean) {
  if (isPrimary) {
    return [propertyState];
  }
  return getCrossLicensedLocale(propertyState) ?? [propertyState];
}

function mapAgentSelectOptionToContactSuggestion(
  agent: AgentSelectOption,
  teamId?: string,
): ContactSuggestion | undefined {
  const group =
    agent.teamId === teamId || agent.teamIdentityId === teamId
      ? 'Team Contacts'
      : 'Other Contacts';

  return {
    userId: agent.id,
    googleId: null,
    followUpBossId: null,
    email: agent.email,
    firstName: agent.firstName,
    lastName: agent.lastName,
    displayName: agent.displayName,
    photoUrl: agent.avatar,
    contactSource: null,
    // The only thing we need downstream is teamId
    contactResponse: { teamId: agent.teamId },
    group,
  };
}

function mapContactSuggestionToAgentSelectOption(
  contact: ContactSuggestion,
): AgentSelectOption | undefined {
  const contactResponse = contact.contactResponse;

  if (
    !contact.userId ||
    !contactResponse?.teamId ||
    !contactResponse.userIdentityId ||
    !contactResponse.teamIdentityId
  ) {
    return undefined;
  }

  return {
    id: contact.userId,
    teamId: contactResponse.teamId,
    userIdentityId: contactResponse.userIdentityId,
    teamIdentityId: contactResponse.teamIdentityId,
    avatar: contact.photoUrl,
    displayName: contact.displayName,
    email: contact.email ?? '',
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    licenseState: contactResponse.licenseState ?? null,
  };
}

function PrimaryAgentAutocompleteField({
  index,
  state,
  excludeAgentIds,
}: AgentAutocompleteFieldProps) {
  const fieldName = `${NAME}.${index}` as const;
  const trackOfferEvent = useTrackOfferAnalytics();

  const {
    field: { value, onBlur, onChange },
    fieldState: { error, invalid },
  } = useController<CreateOfferFormData, `${typeof NAME}.${number}`>({
    name: fieldName,
    rules: {
      validate: (selectedValue: AutocompleteValue) => {
        return !!selectedValue?.id || 'Required';
      },
    },
  });

  const [selectedAgent, setSelectedAgent] = useState<AutocompleteValue>(value);

  const agentDslFilters = useAgentDslFilters({
    licenseStates: state ? getAvailableStatesForAgent(state, true) : undefined,
  });
  const currentlySelectedUserId = value?.id;
  const { agents: initialAgents } = useAgentAutocompleteOptions({
    search: '',
    filters: agentDslFilters,
    userIdsForDefaultQuery: currentlySelectedUserId
      ? [currentlySelectedUserId]
      : [],
  });

  useEffect(() => {
    if (
      !initialAgents.length ||
      selectedAgent ||
      !value ||
      typeof value !== 'object' ||
      !value.id
    )
      return;

    const agent = initialAgents.find(
      ({ teamId, id }) =>
        formatTeamMemberId(id, teamId) ===
        formatTeamMemberId(value.id, value.teamId),
    );

    if (agent) {
      setSelectedAgent(agent);
    }
  }, [initialAgents, selectedAgent, value]);

  // Sync selectedAgent with value changes
  useEffect(() => {
    if (value?.id) {
      setSelectedAgent(value);
    } else {
      setSelectedAgent(DEFAULT_VALUE);
    }
  }, [value]);

  const handleAgentSelection = (
    _: SyntheticEvent,
    agent: AutocompleteValue | null,
  ) => {
    setSelectedAgent(agent ?? DEFAULT_VALUE);
    onChange(agent ?? DEFAULT_VALUE);

    if (agent) {
      trackOfferEvent(TrackOfferEvent.SelectBuyerAgent, {
        agentId: agent.id,
        agentIndex: index,
      });
    } else {
      trackOfferEvent(TrackOfferEvent.ClearBuyerAgent, {
        agentId: selectedAgent?.id ?? null,
        agentIndex: index,
      });
    }
  };

  return (
    <AgentAutocomplete
      id={fieldName}
      onChange={handleAgentSelection}
      selectedAgent={selectedAgent}
      TextFieldProps={{
        label: 'Agent Name',
        placeholder: 'Search and select',
        name: fieldName,
        required: true,
        highlight: selectedAgent ? undefined : 'secondary',
        helperText: error?.message,
        error: invalid,
        onBlur,
      }}
      filters={agentDslFilters}
      excludeAgentIds={excludeAgentIds}
    />
  );
}

function CoAgentAutocompleteField({
  index,
  state,
  excludeAgentIds,
}: AgentAutocompleteFieldProps) {
  const fieldName = `${NAME}.${index}` as const;
  const trackOfferEvent = useTrackOfferAnalytics();
  const userId = useSession()?.data?.user?.id;
  const { memberships } = useUserTeamMembers(userId);
  const teamId = memberships[0]?.teamId;

  const {
    field: { value, onBlur, onChange },
    fieldState: { error, invalid },
  } = useController<CreateOfferFormData, `${typeof NAME}.${number}`>({
    name: fieldName,
  });

  const licenseStates = useMemo(
    () => getAvailableStatesForAgent(state, false),
    [state],
  );

  const handleAgentSelection = (contact: ContactSuggestion) => {
    const agent = mapContactSuggestionToAgentSelectOption(contact);
    onChange(agent ?? DEFAULT_VALUE);

    if (agent) {
      trackOfferEvent(TrackOfferEvent.SelectBuyerAgent, {
        agentId: agent.id,
        agentIndex: index,
      });
    }
  };

  const handleClear = () => {
    trackOfferEvent(TrackOfferEvent.ClearBuyerAgent, {
      agentId: value?.id ?? null,
      agentIndex: index,
    });
    onChange(DEFAULT_VALUE);
  };

  const defaultValue = useMemo(() => {
    return value
      ? mapAgentSelectOptionToContactSuggestion(value, teamId)
      : undefined;
  }, [value, teamId]);

  if (!userId) return null;

  return (
    <CoAgentAutocomplete
      userId={userId}
      teamId={teamId}
      licenseStates={licenseStates}
      excludeAgentIds={excludeAgentIds}
      id={fieldName}
      label="Agent Name"
      name={fieldName}
      placeholder="Search and select"
      error={invalid}
      helperText={error?.message}
      onBlur={onBlur}
      onSelectOption={handleAgentSelection}
      onClear={handleClear}
      defaultValue={defaultValue}
    />
  );
}

export const BuyerAgentsAutocomplete = () => {
  const { getValues } = useFormContext<CreateOfferFormData>();
  const state = useWatch<CreateOfferFormData, 'state'>({ name: 'state' });
  const trackOfferEvent = useTrackOfferAnalytics();

  const { fields, append, remove, replace } = useFieldArray({
    name: NAME,
  });

  const watchedAgents = useWatch<CreateOfferFormData, typeof NAME>({
    name: NAME,
  });

  const excludeAgentIds = useMemo(() => {
    const agents = watchedAgents ?? [];
    return (
      agents
        ?.filter((agent): agent is AgentSelectOption => Boolean(agent?.id))
        .map((agent) => formatTeamMemberId(agent.id, agent.teamId)) || []
    );
  }, [watchedAgents]);

  const handleAddAgent = () => {
    trackOfferEvent(TrackOfferEvent.ClickAddBuyer, {
      buyerOrder: fields.length + 1,
    });
    append(DEFAULT_VALUE);
  };

  const handleRemoveAgent = (index: number) => {
    if (fields.length > 1) {
      trackOfferEvent(TrackOfferEvent.ClickRemoveBuyer, {
        buyerOrder: index + 1,
      });
      remove(index);
    }
  };

  const items = fields.map((field, index) => {
    const agents = watchedAgents ?? [];
    const currentAgentId = agents?.[index]?.id
      ? formatTeamMemberId(agents[index].id, agents[index].teamId)
      : undefined;

    const otherSelectedAgentIds = excludeAgentIds.filter(
      (id) => id !== currentAgentId,
    );

    const isPrimary = index === 0;

    return {
      id: field.id,
      label: fields.length > 1 ? `Buyer Agent ${index + 1}` : undefined,
      content: isPrimary ? (
        <PrimaryAgentAutocompleteField
          index={index}
          state={state ?? ''}
          excludeAgentIds={otherSelectedAgentIds}
        />
      ) : (
        <CoAgentAutocompleteField
          index={index}
          state={state ?? ''}
          excludeAgentIds={otherSelectedAgentIds}
        />
      ),
      onRemove: fields.length > 1 ? () => handleRemoveAgent(index) : undefined,
    };
  });

  // Remove agents that are no longer valid if property state changes
  useEffect(() => {
    const selectedAgents = getValues(NAME) ?? [];
    const filteredAgents = selectedAgents.filter(
      (agent: AutocompleteValue, index: number) =>
        agent?.licenseState &&
        state &&
        getAvailableStatesForAgent(state, index === 0).includes(
          agent.licenseState,
        ),
    );

    replace(filteredAgents.length ? filteredAgents : [DEFAULT_VALUE]);
  }, [state, replace, getValues]);

  return (
    <NewBuyerFormGroup
      items={items}
      maxItems={MAX_CLIENT_GROUP_AGENTS}
      addItemLabel="Add Agent"
      onAddItem={handleAddAgent}
      itemSpacing={4}
    />
  );
};
